import { PlaybackSessionApi } from '../api/playback/session'
import { SessionStorage } from '../../utils/storage'

import SDK_EVENTS from '../../events/bus'
import { SDK_CONCURRENT_PLAYBACK_STOP } from '../../events/types/sdk.events'

import { CONCURRENCY_PLAYBACK_TOKEN_KEY } from '../../constants'

import google from './firebase'

declare type TokenUnsubscribe = () => void

export class PlaybackSessionConcurrency {
  readonly userId: number
  readonly uguid: string

  api: PlaybackSessionApi
  unsubscribe: TokenUnsubscribe | null

  constructor(userId: number, uguid: string, api?: PlaybackSessionApi) {
    this.userId = userId
    this.uguid = uguid

    this.api = api || new PlaybackSessionApi('playback')

    this.unsubscribe = null
    this.handleSnapshot = this.handleSnapshot.bind(this)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleSnapshot(snapshot: Record<string, any>) {
    // if token is expired on the server
    // fromCache needed to ignore offline callbacks that can happen when Firebase is unavailable/blocked
    if (!snapshot.metadata.fromCache && !snapshot.exists) {
      // then end playing session
      this.onTokenExpired()
    }
  }

  private onTokenReceived(token: string): void {
    if (token) {
      SessionStorage.setItem(CONCURRENCY_PLAYBACK_TOKEN_KEY, token)

      this.unsubscribe = google().firestore
        .doc(token)
        .onSnapshot(this.handleSnapshot)
    }
  }

  private onTokenExpired(): void {
    this.unsubscribe && this.unsubscribe()
    this.unsubscribe = null

    SessionStorage.removeItem(CONCURRENCY_PLAYBACK_TOKEN_KEY)
    SDK_EVENTS.dispatchEvent(SDK_CONCURRENT_PLAYBACK_STOP)
  }

  async start(): Promise<void> {
    // do nothing if already listening to the token changes
    if (this.unsubscribe) return
    try {
      const token: string | null = SessionStorage.getItem(CONCURRENCY_PLAYBACK_TOKEN_KEY)

      // otherwise call back-end for a new playback session (or a continuation of a saved one)
      const playbackToken = await this.startSession(token)

      this.onTokenReceived(playbackToken)
    } catch (error) {
      console.error('Unable to start playback concurrency session:', error.message)
      throw error
    }
  }

  async startSession(token: string | null): Promise<string> {
    try {
      return await this.api.fetchToken(token, this.uguid, this.userId)
    } catch (error) {
      console.error(error)
      return ''
    }
  }
}
